import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import "./GoogleCalander.css";
import { ChevronRight, Phone } from "lucide-react";

// Toggle mock mode for testing
const MOCK_MODE = false;

// Utility: sanitize Google Calendar link
const sanitizeLink = (url) =>
  url?.replace(/\/{2,}/g, "/").replace("https:/", "https://");

function GoogleCalendar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Connecting to Google Calendar...");
  const [calendarLink, setCalendarLink] = useState(null);

  useEffect(() => {
    const handleCalendarConnection = async () => {
      const queryParams = new URLSearchParams(location.search);
      const state = queryParams.get("state");
      const authCode = queryParams.get("code");
      const directAppointmentId = queryParams.get("appointment_id");

      const token = localStorage.getItem("authToken");
      const isAuthRequired = localStorage.getItem("requires_auth") === "true";

      // Appointment ID priority: state → direct param → localStorage
      const appointmentId =
        (state && /^\d+$/.test(state) && state) ||
        directAppointmentId ||
        localStorage.getItem("appointmentId");

      if (!appointmentId) {
        setStatus("error");
        setMessage("Missing appointment ID.");
        return;
      }

      // Retrieve cached data
      const alreadyCreated = localStorage.getItem(
        `calendarEventCreated_${appointmentId}`
      );
      const alreadyUpdated = localStorage.getItem(
        `calendarEventUpdated_${appointmentId}`
      );
      const savedLink = localStorage.getItem(
        `calendarLink_${appointmentId}`
      );

      // Detect if we are in reschedule mode (PUT)
      const isReschedule = !!directAppointmentId && !authCode;

      // Skip only if we are not rescheduling and we have a saved link
      if (!isReschedule && (alreadyCreated || alreadyUpdated) && savedLink) {
        setCalendarLink(savedLink);
        setStatus("success");
        setMessage("Google Calendar event already handled.");
        return;
      }

      // MOCK MODE (testing)
      if (MOCK_MODE) {
        const mockLink =
          "https://www.google.com/calendar/event?eid=mock1234567890";
        setTimeout(() => {
          const cleanLink = sanitizeLink(mockLink);
          setCalendarLink(cleanLink);
          setStatus("success");
          setMessage("Google Calendar event created (mock)!");
          localStorage.setItem(
            `calendarLink_${appointmentId}`,
            cleanLink
          );
          localStorage.setItem(
            `calendarEventCreated_${appointmentId}`,
            "true"
          );
        }, 1000);
        return;
      }

      try {
        if (authCode && state) {
          /** --------------------
           * 1. OAuth Callback Flow
           * -------------------- */
          const callbackRes = await axios.get(
            `${process.env.REACT_APP_BASE_URLOPEN}google-calendar/callback?code=${authCode}&state=${state}`
          );

          if (callbackRes.data?.calendar === "connected") {
            const eventRes = await axios.post(
              `${process.env.REACT_APP_BASE_URLOPEN}google-calendar/event`,
              { appointment_id: Number(appointmentId) },
              {
                headers: {
                  "Content-Type": "application/json",
                },
              }
            );

            const link = eventRes?.data?.event?.multiple
              ? eventRes.data.event.events?.[0]?.link
              : eventRes?.data?.event?.link;

            if (link) {
              const cleanLink = sanitizeLink(link);
              setCalendarLink(cleanLink);
              setStatus("success");
              setMessage("Google Calendar event created successfully!");
              localStorage.setItem(
                `calendarLink_${appointmentId}`,
                cleanLink
              );
              localStorage.setItem(
                `calendarEventCreated_${appointmentId}`,
                "true"
              );
            } else {
              setStatus("error");
              setMessage("Event created but no calendar link found.");
            }
          } else {
            setStatus("error");
            setMessage("Google Calendar connection failed.");
          }
        } else if (!isAuthRequired && appointmentId) {
          /** --------------------
           * 2. Reschedule / PUT Flow
           * -------------------- */

            if (directAppointmentId) {
              // Clear only relevant localStorage for this appointment
              localStorage.removeItem(`calendarEventCreated_${appointmentId}`);
              localStorage.removeItem(`calendarEventUpdated_${appointmentId}`);
              localStorage.removeItem(`calendarLink_${appointmentId}`);
            }
          const eventRes = await axios.put(
            `${process.env.REACT_APP_BASE_URLOPEN}google-calendar/event`,
            { appointment_id: Number(appointmentId) },
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          const link = eventRes?.data?.event?.multiple
            ? eventRes.data.event.events?.[0]?.link
            : eventRes?.data?.event?.link;

          if (link) {
            const cleanLink = sanitizeLink(link);
            setCalendarLink(cleanLink);
            setStatus("success");
            setMessage("Google Calendar event updated successfully!");
            localStorage.setItem(
              `calendarLink_${appointmentId}`,
              cleanLink
            );
            localStorage.setItem(
              `calendarEventUpdated_${appointmentId}`,
              "true"
            );
          } else {
            setStatus("error");
            setMessage("Event updated but no calendar link found.");
          }
        } else {
          setStatus("error");
          setMessage("Missing authorization code or appointment ID.");
        }
      } catch (error) {
        console.error("Error:", error);
        setStatus("error");
        setMessage(
          error.response?.data?.message ||
            "Something went wrong while connecting to Google Calendar."
        );
      }
    };

    handleCalendarConnection();
  }, [location.search, navigate]);

  return (
    <div className="google-calendar-container">
      <header className="header">
        <div className="logo">
          <img src={"https://pbldcbe.webdezign.uk/static/media/ldc_logo_new.1f2a7d791d5f16307994.png"} alt="Logo" className="logo-img" />
        </div>
        <div className="desktop-only">
          <div className="header-link">
            <ChevronRight size={12} />
            <span>Book online</span>
          </div>
          <div className="contact-call">
            <ChevronRight size={12} />
            <span>Or call 020 3993 0813</span>
          </div>
        </div>
        <div className="mobile-only">
          <span className="phone-number">
            <Phone size={20} />
          </span>
          <button className="hamburger">☰</button>
        </div>
      </header>

      <div className="google-calendar-loader">
        {status === "loading" && <div className="spinner"></div>}
        <p>{message}</p>
        {status === "success" && calendarLink && (
          <a
            href={calendarLink}
            target="_blank"
            rel="noopener noreferrer"
            className="calendar-button"
          >
            View in Google Calendar
          </a>
        )}

        {(status === "success" || status === "error") && (
          <button onClick={() => navigate("/")} className="go-back-button">
            Go Back
          </button>
        )}
      </div>

      <footer className="footer">
        <div className="logo" style={{ display: "flex", justifyContent: "center" }}>
          <img src={"https://pbldcbe.webdezign.uk/static/media/ldc_logo_new.1f2a7d791d5f16307994.png"} alt="Logo" style={{ height: "40px" }} />
        </div>
      </footer>
    </div>
  );
}

export default GoogleCalendar;
